/** Display helpers for the users page. Pure functions: no React, no network. */

export const ROLES = [
  { value: 'user', label: 'User', description: 'Views the dashboards you choose below.' },
  { value: 'admin', label: 'Admin', description: 'Manages users, data, reports and every dashboard in their companies.' },
  { value: 'super_admin', label: 'Super admin', description: 'Full access to every company, including managing companies.' },
];

export const MIN_PASSWORD_LENGTH = 12;

export const roleLabel = (role) => ROLES.find((r) => r.value === role)?.label || role;

export const displayName = (user) => user?.full_name || user?.username || '';

/** Up to two letters for the avatar. */
export function initials(user) {
  const words = displayName(user).trim().split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return '?';
  return (words.length === 1 ? words[0].slice(0, 2) : `${words[0][0]}${words[words.length - 1][0]}`).toUpperCase();
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "Just now", "3 hours ago", "12 days ago", then the date; "Never" if they have not signed in. */
export function lastSignIn(value, now = Date.now()) {
  if (!value) return 'Never';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return 'Never';
  const ago = now - then;
  const plural = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'} ago`;
  if (ago < MINUTE) return 'Just now';
  if (ago < HOUR) return plural(Math.floor(ago / MINUTE), 'minute');
  if (ago < DAY) return plural(Math.floor(ago / HOUR), 'hour');
  if (ago < 30 * DAY) return plural(Math.floor(ago / DAY), 'day');
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** What a user can see, as a short phrase for the table. */
export function dashboardAccess(user) {
  if (user.role !== 'user') return 'All dashboards';
  const n = user.dashboard_count || 0;
  if (n === 0) return 'No dashboards';
  return `${n} dashboard${n === 1 ? '' : 's'}`;
}

/** The companies line for the table: names up to a limit, then a count of the rest. */
export function companyAccess(user, limit = 2) {
  if (user.role === 'super_admin') return 'All companies';
  const names = (user.companies || []).map((c) => c.name);
  if (names.length === 0) return 'No companies';
  if (names.length <= limit) return names.join(', ');
  return `${names.slice(0, limit).join(', ')} +${names.length - limit} more`;
}

/** A random password that is easy to read aloud: no 0/O or 1/l/I. */
export function generatePassword(length = 16) {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  // Values at or above this would favour the start of the alphabet.
  const limit = 256 - (256 % alphabet.length);
  const out = [];
  while (out.length < length) {
    for (const byte of crypto.getRandomValues(new Uint8Array(length * 2))) {
      if (byte < limit && out.length < length) out.push(alphabet[byte % alphabet.length]);
    }
  }
  return out.join('');
}
